import { forwardRef, useId, useState, useEffect, useRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { ChevronUp, ChevronDown, AlertCircle, CheckCircle2 } from 'lucide-react';

export interface AmountInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value'> {
  /** Etiqueta sobre el campo */
  label?: string;
  /** Valor numérico controlado */
  value?: number;
  /** Callback con el nuevo valor */
  onChange?: (value: number) => void;
  /** Prefijo visible dentro del campo (ej. "S/", "$", "%") */
  prefix?: string;
  /** Paso de incremento/decremento (default: 1) */
  step?: number;
  /** Valor mínimo (default: 0) */
  min?: number;
  /** Valor máximo */
  max?: number;
  /** Tamaño del componente */
  size?: 'sm' | 'md' | 'lg';
  /** Estado visual */
  state?: 'default' | 'error' | 'success' | 'warning';
  /** Mensaje de ayuda o error */
  helperText?: string;
  /** Si el campo es requerido */
  required?: boolean;
  /** Clases adicionales para el contenedor */
  containerClassName?: string;
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>((
  {
    label,
    value = 0,
    onChange,
    prefix,
    step = 1,
    min = 0,
    max,
    size = 'md',
    state = 'default',
    helperText,
    required,
    disabled,
    containerClassName = '',
    id: idProp,
    onFocus,
    onBlur,
    ...props
  },
  ref
) => {
  const generatedId = useId();
  const inputId = idProp ?? generatedId;

  // ── Display value: texto libre mientras escribe, formateado al salir ────────
  const [displayValue, setDisplayValue] = useState(() => value.toFixed(2));
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value]);

  // ── Clamp ──────────────────────────────────────────────────────────────────
  const clamp = (v: number) => {
    let result = parseFloat(v.toFixed(10));
    result = Math.max(result, min);
    if (max !== undefined) result = Math.min(result, max);
    return result;
  };

  const isAtMin = value <= min;
  const isAtMax = max !== undefined && value >= max;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocused.current = true;
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocused.current = false;
    const parsed = parseFloat(displayValue);
    const final = Number.isNaN(parsed) ? min : clamp(parsed);
    setDisplayValue(final.toFixed(2));
    onChange?.(final);
    onBlur?.(e);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setDisplayValue(raw);
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      onChange?.(clamp(parsed));
    }
  };

  const handleIncrement = () => {
    if (disabled || isAtMax) return;
    const next = clamp(value + step);
    setDisplayValue(next.toFixed(2));
    onChange?.(next);
  };

  const handleDecrement = () => {
    if (disabled || isAtMin) return;
    const next = clamp(value - step);
    setDisplayValue(next.toFixed(2));
    onChange?.(next);
  };

  // ── Tamaños ────────────────────────────────────────────────────────────────
  const heightClass  = { sm: 'h-10', md: 'h-14', lg: 'h-[72px]' }[size];
  const valueFont    = { sm: 'text-xl font-semibold', md: 'text-2xl font-semibold', lg: 'text-3xl font-bold' }[size];
  const prefixFont   = { sm: 'text-ui-sm', md: 'text-ui-base', lg: 'text-body' }[size];
  const spinnerWidth = { sm: 'w-8', md: 'w-9', lg: 'w-10' }[size];
  const chevronSize  = { sm: 'w-3 h-3', md: 'w-3.5 h-3.5', lg: 'w-4 h-4' }[size];
  const prefixWidth  = { sm: 'w-10', md: 'w-11', lg: 'w-12' }[size];
  const paddingLeft  = prefix
    ? { sm: 'pl-11', md: 'pl-12', lg: 'pl-14' }[size]
    : 'pl-4';

  // ── Estado ─────────────────────────────────────────────────────────────────
  const borderClass = {
    default: 'border-strong focus-within:ring-2 focus-within:ring-primary',
    error:   'border-2 border-error   focus-within:ring-2 focus-within:ring-error',
    success: 'border-2 border-success focus-within:ring-2 focus-within:ring-success',
    warning: 'border-2 border-warning focus-within:ring-2 focus-within:ring-warning',
  }[state];

  const helperColor = {
    default: 'text-tertiary',
    error:   'text-error',
    success: 'text-success',
    warning: 'text-warning',
  }[state];

  const HelperIcon = {
    default: null,
    error:   AlertCircle,
    success: CheckCircle2,
    warning: AlertCircle,
  }[state];

  return (
    <div className={containerClassName}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className="block text-ui-sm font-medium mb-1.5 text-tertiary"
        >
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </label>
      )}

      {/* Input wrapper */}
      <div
        className={`relative flex rounded-lg border bg-surface-0 transition-all duration-200 ${borderClass} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {/* Prefix */}
        {prefix && (
          <div
            className={`absolute left-0 top-0 bottom-0 flex items-center justify-center border-r border-default bg-surface-1 rounded-l-lg ${prefixWidth}`}
          >
            <span className={`${prefixFont} font-semibold text-tertiary`}>{prefix}</span>
          </div>
        )}

        {/* Input */}
        <input
          ref={ref}
          id={inputId}
          type="text"
          inputMode="decimal"
          value={displayValue}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={label}
          aria-invalid={state === 'error'}
          aria-describedby={helperText ? `${inputId}-helper` : undefined}
          className={`flex-1 ${heightClass} ${paddingLeft} pr-2 bg-transparent ${valueFont} text-primary focus:outline-none`}
          {...props}
        />

        {/* Spinner */}
        <div
          className={`flex flex-col border-l border-default overflow-hidden ${spinnerWidth} rounded-r-lg flex-shrink-0`}
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={handleIncrement}
            disabled={disabled || isAtMax}
            aria-label="Incrementar"
            className="flex-1 flex items-center justify-center text-tertiary hover:bg-surface-1 hover:text-primary active:bg-surface-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-tr-lg"
          >
            <ChevronUp className={chevronSize} strokeWidth={2.5} />
          </button>
          <div className="h-px bg-border-default flex-shrink-0" />
          <button
            type="button"
            tabIndex={-1}
            onClick={handleDecrement}
            disabled={disabled || isAtMin}
            aria-label="Decrementar"
            className="flex-1 flex items-center justify-center text-tertiary hover:bg-surface-1 hover:text-primary active:bg-surface-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-br-lg"
          >
            <ChevronDown className={chevronSize} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Helper */}
      {helperText && (
        <div
          id={`${inputId}-helper`}
          className={`flex items-center gap-1.5 mt-1.5 text-caption ${helperColor}`}
        >
          {HelperIcon && <HelperIcon size={13} />}
          <span>{helperText}</span>
        </div>
      )}
    </div>
  );
});

AmountInput.displayName = 'AmountInput';


